import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Building2, Search, Filter, ChevronLeft, ChevronRight,
  Clock, CheckCircle, XCircle, PauseCircle, AlertCircle, Users, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const statusConfig = {
  trialing: { label: 'Trial', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  active: { label: 'Aktiv', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  past_due: { label: 'Überfällig', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: AlertCircle },
  cancelled: { label: 'Gekündigt', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle },
  suspended: { label: 'Gesperrt', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: PauseCircle },
  expired: { label: 'Abgelaufen', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const planLabels = {
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise'
};

export default function AdminAccounts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [planFilter, setPlanFilter] = useState(searchParams.get('plan') || '');

  useEffect(() => {
    fetchAccounts();
  }, [searchParams]);

  const fetchAccounts = async () => {
    setLoading(true);
    const token = localStorage.getItem('cashx_token');
    
    const params = new URLSearchParams();
    if (searchParams.get('search')) params.set('search', searchParams.get('search'));
    if (searchParams.get('status')) params.set('status', searchParams.get('status'));
    if (searchParams.get('plan')) params.set('plan', searchParams.get('plan'));
    params.set('page', searchParams.get('page') || '1');
    params.set('limit', '20');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/accounts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch accounts');
      
      const data = await response.json();
      setAccounts(data.accounts);
      setPagination({
        page: data.page,
        pages: data.pages,
        total: data.total
      });
    } catch (error) {
      toast.error('Fehler beim Laden der Accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams);
    if (search) params.set('search', search);
    else params.delete('search');
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value && value !== 'all') params.set(key, value);
    else params.delete(key);
    params.set('page', '1');
    setSearchParams(params);
    
    if (key === 'status') setStatusFilter(value);
    if (key === 'plan') setPlanFilter(value);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="p-6 md:p-8">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-2xl md:text-3xl font-bold"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          data-testid="admin-accounts-title"
        >
          Accounts
        </h1>
        <p className="text-slate-400 mt-1">
          {pagination.total} Accounts insgesamt
        </p>
      </div>

      {/* Filters */}
      <Card 
        className="p-4 mb-6" 
        style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
      >
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Suchen nach Firma, E-Mail..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="accounts-search-input"
                className="pl-10 bg-[#0E1628] border-slate-700 text-white"
              />
            </div>
          </form>
          
          {/* Status Filter */}
          <Select 
            value={statusFilter || 'all'} 
            onValueChange={(v) => handleFilterChange('status', v)}
          >
            <SelectTrigger 
              className="w-full md:w-48 bg-[#0E1628] border-slate-700"
              data-testid="accounts-status-filter"
            >
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#111C32] border-slate-700">
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="trialing">Trial</SelectItem>
              <SelectItem value="active">Aktiv</SelectItem>
              <SelectItem value="past_due">Überfällig</SelectItem>
              <SelectItem value="cancelled">Gekündigt</SelectItem>
              <SelectItem value="suspended">Gesperrt</SelectItem>
              <SelectItem value="expired">Abgelaufen</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Plan Filter */}
          <Select 
            value={planFilter || 'all'} 
            onValueChange={(v) => handleFilterChange('plan', v)}
          >
            <SelectTrigger 
              className="w-full md:w-48 bg-[#0E1628] border-slate-700"
              data-testid="accounts-plan-filter"
            >
              <SelectValue placeholder="Plan" />
            </SelectTrigger>
            <SelectContent className="bg-[#111C32] border-slate-700">
              <SelectItem value="all">Alle Pläne</SelectItem>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Accounts List */}
      <Card style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}>
        {loading ? (
          <div className="p-4 space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-20 w-full bg-slate-700 rounded-lg" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Building2 className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">Keine Accounts gefunden</p>
            <p className="text-sm mt-1">Versuchen Sie andere Suchkriterien</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {accounts.map((account) => {
              const config = statusConfig[account.account_status] || statusConfig.active;
              const StatusIcon = config.icon;
              
              return (
                <Link
                  key={account.tenant_id}
                  to={`/admin/accounts/${account.tenant_id}`}
                  className="flex items-center justify-between p-4 hover:bg-slate-800/30 transition-colors"
                  data-testid={`account-row-${account.tenant_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-slate-300" />
                    </div>
                    <div>
                      <p className="font-medium">{account.company_name}</p>
                      <p className="text-sm text-slate-400">{account.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                          {planLabels[account.plan] || account.plan}
                        </Badge>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {account.user_count || 0} User
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right hidden md:block">
                      <p className="text-sm text-slate-400">Erstellt</p>
                      <p className="text-sm">{formatDate(account.created_at)}</p>
                    </div>
                    <Badge className={`${config.color} border`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {config.label}
                    </Badge>
                    <Eye className="h-5 w-5 text-slate-500" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-800">
            <p className="text-sm text-slate-400">
              Seite {pagination.page} von {pagination.pages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
